import { DoublyLinkedList } from './doubly-linked-list.js'
import type { Node } from './node.js'

/**
 * Par clave-valor almacenado en cada nodo del caché.
 */
interface CacheEntry<K, V> {
  key: K
  value: V
}

/**
 * Caché LRU (Least Recently Used) de capacidad fija.
 * Combina un DoublyLinkedList (para rastrear orden de uso) con un Map (para acceso O(1) por clave).
 * El nodo al inicio de la lista es el menos usado recientemente; el del final, el más reciente.
 * Cuando la capacidad está llena, el elemento menos usado es evictado automáticamente.
 * @template K - Tipo de las claves.
 * @template V - Tipo de los valores.
 */
export class LRUCache<K, V> {
  readonly #capacity: number
  readonly #list = new DoublyLinkedList<CacheEntry<K, V>>()
  readonly #map = new Map<K, Node<CacheEntry<K, V>>>()

  constructor(capacity: number) {
    if (capacity < 1) {
      throw new RangeError(`La capacidad debe ser al menos 1, recibido: ${capacity}`)
    }
    this.#capacity = capacity
  }

  /**
   * Obtiene el valor de la clave y la marca como la más recientemente usada.
   * @returns El valor, o undefined si la clave no existe.
   */
  get(key: K): V | undefined {
    const node = this.#map.get(key)
    if (!node) return undefined
    const { value } = node.value
    this.#list.removeNode(node)
    this.#list.push({ key, value })
    this.#map.set(key, this.#list.tail!)
    return value
  }

  /**
   * Inserta o actualiza un par clave-valor.
   * Si la capacidad está llena, evicta el elemento menos recientemente usado.
   */
  put(key: K, value: V): void {
    if (this.#map.has(key)) {
      const node = this.#map.get(key)!
      this.#list.removeNode(node)
      this.#list.push({ key, value })
      this.#map.set(key, this.#list.tail!)
      return
    }
    if (this.#list.size >= this.#capacity) {
      const lruKey = this.#list.head!.value.key
      this.#list.shift()
      this.#map.delete(lruKey)
    }
    this.#list.push({ key, value })
    this.#map.set(key, this.#list.tail!)
  }

  /**
   * Comprueba si la clave existe en el caché sin modificar el orden de uso.
   */
  has(key: K): boolean {
    return this.#map.has(key)
  }

  /**
   * Elimina una entrada del caché.
   * @returns true si existía, false si no.
   */
  delete(key: K): boolean {
    const node = this.#map.get(key)
    if (!node) return false
    this.#list.removeNode(node)
    this.#map.delete(key)
    return true
  }

  /**
   * Vacía el caché completamente.
   */
  clear(): void {
    this.#list.clear()
    this.#map.clear()
  }

  /**
   * Número de entradas actuales en el caché.
   */
  get size(): number {
    return this.#list.size
  }

  /**
   * Capacidad máxima del caché.
   */
  get capacity(): number {
    return this.#capacity
  }

  /**
   * Indica si el caché está vacío.
   */
  isEmpty(): boolean {
    return this.#list.size === 0
  }

  /**
   * Retorna las entradas en orden de más reciente a menos reciente.
   */
  entries(): [K, V][] {
    const arr = this.#list.toArray()
    const result: [K, V][] = []
    for (let i = arr.length - 1; i >= 0; i--) {
      result.push([arr[i].key, arr[i].value])
    }
    return result
  }
}
